import React, { useEffect, useState } from 'react';
import { Button } from '@asigna/kit';
import classNames from 'classnames';
import { MAX_NOTES_LENGTH, NotesInput } from 'components';
import useNetwork from 'hooks/use-network';
import useSafe from 'hooks/use-safe';
import { useSafeCalls } from 'hooks/use-safe-call';
import { isEmpty } from 'lodash';
import { NETWORK } from 'types/enums';

import { AddressBalance, isValidRecipient } from '../../../../utils';
import { getAllCollectionNftIds } from '../../../../utils/nft-tools';
import { generateSendBatchNft, generateSendNft } from '../../../../utils/transaction-create';
import { InputAndDisplayNfts } from '../../../InputAndDisplayNfts/InputAndDisplayNfts';
import { SelectCollectionField } from '../../../SelectCollectionField/SelectCollectionField';
import { SetTxFee } from '../../components/SetTxFee';
import DropDownList from '../DropDownList';
import { NonceInput } from '../NonceInput';
import { TransactionCommonDialog } from '../TransactionCommonDialog';

import { BATCH_TRANSFER_DISABLED } from './constants';
import { useSendNftDialogStyles } from './SendNftDialogStyles';

interface TransactionCommonProps {
  open: boolean;
  preselectedCollectionId: string;
  preselectedNFTIds: string[] | null;
  selectAllIds?: boolean;
  balance: Pick<AddressBalance, 'non_fungible_tokens'>;
  onClose: () => void;
}

// TODO_R: move into NFTsTab/components
// TODO_R: [FRS]
export const SendNftDialog = (props: TransactionCommonProps) => {
  const { open, onClose, preselectedNFTIds, preselectedCollectionId, selectAllIds, balance } =
    props;
  const { safe } = useSafe();
  const safeAddress = safe.address;
  const [selectedCollectionId, setSelectedCollectionId] = useState(preselectedCollectionId);
  const [selectedNFTIds, setSelectedNFTIds] = useState<string[]>(preselectedNFTIds || []);
  const [recepientAddress, setRecepientAddress] = useState('');
  const [transactionNonce, setTransactionNonce] = useState('');
  const [step, setStep] = useState<1 | 2>(1);
  const [network, stacksNetwork] = useNetwork();
  const classes = useSendNftDialogStyles();
  const { nativeSendBatchNFT, nativeSendNFT } = useSafeCalls();
  const [errorMsg, setErrorMsg] = useState('');
  const [collectionContract, collectionName] = selectedCollectionId.split('::');
  const [generatedTxHex, setGeneratedTxHex] = useState('');
  const [collectionDropDownOpened, setCollectionDropDownOpened] = useState(false);
  const [nftImagesDropdownOpened, setNFTImagesDropdownOpened] = useState(false);
  const [notesValue, setNotesValue] = useState('');

  // TODO_R: remove this effect after moving into @material-ui Select for CollectionSelect and NFTImagesSelect.
  // this thing works out of the box there.
  // Then move collectionDropDownOpened and nftImagesDropdownOpened states into their components themself
  useEffect(() => {
    if (collectionDropDownOpened) {
      setNFTImagesDropdownOpened(false);

      return;
    }
    if (nftImagesDropdownOpened) setCollectionDropDownOpened(false);
  }, [collectionDropDownOpened, nftImagesDropdownOpened]);

  useEffect(() => {
    if (selectAllIds) {
      if (BATCH_TRANSFER_DISABLED) {
        return;
      }
      getAllCollectionNftIds(safe.address, stacksNetwork, selectedCollectionId).then((res) => {
        setSelectedNFTIds(res);
      });
    }
  }, []);

  const handleCollectionChange = (value: string) => {
    setSelectedCollectionId(value);
    setSelectedNFTIds([]);
  };

  const handleNextStepClick = () => {
    const generateTx =
      selectedNFTIds.length > 1
        ? () =>
            generateSendBatchNft(
              +transactionNonce,
              collectionContract,
              selectedNFTIds.map((number) => +number),
              recepientAddress,
              network as NETWORK,
              stacksNetwork,
              safe,
              collectionName,
              0
            )
        : () =>
            generateSendNft(
              +transactionNonce,
              collectionContract,
              +selectedNFTIds[0],
              recepientAddress,
              stacksNetwork,
              safe,
              collectionName,
              0
            );

    generateTx().then((res) => {
      setGeneratedTxHex(res);
      setStep(2);
    });
  };

  const handleSend = async (gasfee: number) => {
    if (!selectedCollectionId || selectedNFTIds.length < 1) {
      return;
    }
    if (selectedNFTIds.length > 1) {
      await nativeSendBatchNFT(
        +transactionNonce,
        collectionContract,
        selectedNFTIds.map((number) => +number),
        recepientAddress,
        collectionName,
        gasfee,
        notesValue
      );

      return;
    }
    await nativeSendNFT(
      +transactionNonce,
      collectionContract,
      +selectedNFTIds[0],
      recepientAddress,
      collectionName,
      gasfee,
      notesValue
    );
    onClose();
  };

  const addOrDeleteIdHandler = (selectedNFTIds: string[]) => {
    if (isEmpty(selectedNFTIds)) setSelectedNFTIds([]);
    if (BATCH_TRANSFER_DISABLED) {
      setSelectedNFTIds(selectedNFTIds);

      return;
    }
    setSelectedNFTIds((previousNFTIds) => {
      if (selectedNFTIds.length === 1 && previousNFTIds.includes(selectedNFTIds[0]))
        return previousNFTIds.filter((nftId) => nftId !== selectedNFTIds[0]);

      return Array.from(new Set([...previousNFTIds, ...selectedNFTIds]));
    });
  };

  const handleDeleteClick = (itemToDelete: string) => {
    setSelectedNFTIds((prevState) => prevState.filter((item) => item !== itemToDelete));
  };

  return (
    <TransactionCommonDialog
      open={open}
      title={step === 1 ? 'Send NFT' : 'Choose Fee'}
      onClose={onClose}
      className={classes.modalRoot}
    >
      {step === 1 && (
        <div className={classes.wrapper}>
          <div className={classes.nonceWrapper}>
            <NonceInput value={transactionNonce} onChange={setTransactionNonce} />
          </div>
          <div className={classes.inputWrapper}>
            <DropDownList
              onChange={
                // TODO_R: move into handleRecepientAddressChange
                (address) => {
                  setRecepientAddress(address);
                  if (address === safeAddress) {
                    setErrorMsg('Current vault address');
                  } else if (address.endsWith('.stxs')) {
                    setErrorMsg('Invalid STXS name');
                  } else if (address.includes('.')) {
                    !isValidRecipient(address) ? setErrorMsg('Invalid BNS name') : setErrorMsg('');
                  } else if (!isValidRecipient(address) && address.length > 0) {
                    setErrorMsg('Invalid address');
                  } else {
                    setErrorMsg('');
                  }
                }
              }
              placeholder="Recipient (address or BNS name)"
              value={recepientAddress}
            />
            {!!errorMsg && <span className={classes.error}>{errorMsg}</span>}
          </div>
          <div className={classes.inputWrapper}>
            <SelectCollectionField
              selectedCollection={selectedCollectionId}
              onChange={handleCollectionChange}
              collections={Object.keys(balance.non_fungible_tokens)}
              setCollectionDropDownOpened={setCollectionDropDownOpened}
              collectionDropDownOpened={collectionDropDownOpened}
            />
          </div>
          <div className={classNames(classes.inputWrapper, classes.inputIDSWrapper)}>
            <InputAndDisplayNfts
              addOrDeleteIdHandler={addOrDeleteIdHandler}
              onDeleteClick={handleDeleteClick}
              selectedNFTIds={selectedNFTIds}
              selectedCollectionId={selectedCollectionId}
              nftImagesDropdownOpened={nftImagesDropdownOpened}
              setNFTImagesDropdownOpened={setNFTImagesDropdownOpened}
            />
          </div>
          <NotesInput value={notesValue} onChange={setNotesValue} className="mt-0" />
          <Button
            fullWidth
            onClick={handleNextStepClick}
            disabled={
              selectedNFTIds.length < 1 ||
              !selectedCollectionId ||
              !recepientAddress ||
              notesValue.length > MAX_NOTES_LENGTH
            }
            colorType="primary"
            message="Next"
          />
        </div>
      )}
      {step === 2 && (
        <SetTxFee
          transactionHex={generatedTxHex}
          onSend={handleSend}
          onBackClick={() => setStep(1)}
        />
      )}
    </TransactionCommonDialog>
  );
};
